import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export type TConversationProvider = 'ultravox' | 'heygen';

// Types based on the OpenAPI schema
export interface UserListResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  role_name: string;
  organization_name: string;
}

export interface UserCreateRequest {
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  role_name: string;
  organization_id: string;
  organization_name: string;
}

export interface UserUpdateRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  role_id?: string;
  role_name?: string;
  organization_id?: string;
}

export interface SubscriptionSettings {
  show_lite_plan: boolean;
  show_advanced_plan: boolean;
  show_enterprise_plan: boolean;
  allow_annual_payment: boolean;
  allow_monthly_payment: boolean;
  default_payment_option: 'monthly' | 'annual';
}

export interface OrganizationBase {
  _id: string;
  name: string;
  trial_available: boolean;
  is_virtual: boolean;
  conversation_provider: TConversationProvider;
  subscription_settings?: SubscriptionSettings;
}

export interface OrganizationDetail extends OrganizationBase {
  subscription_settings: SubscriptionSettings;
}

export interface OrganizationCreateUpdate {
  name: string;
  trial_available: boolean;
  conversation_provider: TConversationProvider;
  subscription_settings: SubscriptionSettings;
}

export interface SubscriptionPeriodSummary {
  id: string;
  subscription_plan_name: string;
  organization_name: string;
  total_excluding_tax: number;
  subtype: string;
  period_end: string;
  period_start: string;
  payment_received: boolean;
  purpose_type: string;
  project_limit: number;
  session_limit: number;
}

export interface SubscriptionPeriodCreate {
  subscription_plan_id: string;
  subscription_plan_name: string;
  organization_id: string;
  organization_name: string;
  total_excluding_tax: number;
  period_start: string;
  period_end: string;
  payment_received: boolean;
  type: string;
  purpose_type: string;
  project_limit: number;
  session_limit: number;
}

export interface SubscriptionPeriodUpdate {
  subscription_plan_id?: string;
  subscription_plan_name?: string;
  total_excluding_tax?: number;
  period_start?: string;
  period_end?: string;
  payment_received: boolean;
  type?: string;
  purpose_type?: string;
  project_limit?: number;
  session_limit?: number;
}

export interface SelectBase {
  _id: string;
  name: string;
}

// Create the API client
export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_APP_API_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem('auth_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Users', 'Organizations', 'SubscriptionPeriods', 'Selects'],
  endpoints: (builder) => ({
    // Users
    getUsers: builder.query<UserListResponse[], void>({
      query: () => '/users/',
      providesTags: ['Users'],
    }),
    getUser: builder.query<UserListResponse, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    createUser: builder.mutation<void, UserCreateRequest>({
      query: (body) => ({
        url: '/users/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: builder.mutation<void, { id: string; body: UserUpdateRequest }>({
      query: ({ id, body }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Users', id }, {type: 'Users'}],
    }),

    // Organizations
    getOrganizations: builder.query<OrganizationBase[], void>({
      query: () => '/organizations/',
      providesTags: ['Organizations'],
    }),
    getOrganization: builder.query<OrganizationDetail, string>({
      query: (id) => `/organizations/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Organizations', id }],
    }),
    createOrganization: builder.mutation<void, OrganizationCreateUpdate>({
      query: (body) => ({
        url: '/organizations/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: builder.mutation<void, { id: string; body: OrganizationCreateUpdate }>({
      query: ({ id, body }) => ({
        url: `/organizations/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Organizations', id }, { type: 'Organizations' }],
    }),

    // Subscription Periods
    getSubscriptionPeriods: builder.query<SubscriptionPeriodSummary[], void>({
      query: () => '/subscription_periods/',
      providesTags: ['SubscriptionPeriods'],
    }),
    getSubscriptionPeriod: builder.query<SubscriptionPeriodSummary, string>({
      query: (id) => `/subscription_periods/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'SubscriptionPeriods', id }],
    }),
    createSubscriptionPeriod: builder.mutation<void, SubscriptionPeriodCreate>({
      query: (body) => ({
        url: '/subscription_periods/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SubscriptionPeriods'],
    }),
    updateSubscriptionPeriod: builder.mutation<void, { id: string; body: SubscriptionPeriodUpdate }>({
      query: ({ id, body }) => ({
        url: `/subscription_periods/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'SubscriptionPeriods', id }, { type: 'SubscriptionPeriods' }],
    }),
    deleteSubscriptionPeriod: builder.mutation<void, string>({
      query: (id) => ({
        url: `/subscription_periods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubscriptionPeriods'],
    }),

    // Selects
    getOrganizationsSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/organizations',
      providesTags: ['Selects'],
    }),
    getRolesSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/roles',
      providesTags: ['Selects'],
    }),
    getSubscriptionPlansSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/subscription_plans',
      providesTags: ['Selects'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetSubscriptionPeriodsQuery,
  useGetSubscriptionPeriodQuery,
  useCreateSubscriptionPeriodMutation,
  useUpdateSubscriptionPeriodMutation,
  useDeleteSubscriptionPeriodMutation,
  useGetOrganizationsSelectQuery,
  useGetRolesSelectQuery,
  useGetSubscriptionPlansSelectQuery,
} = api;
